import { motion } from "framer-motion";
import { ChevronDown, Facebook, Instagram, MessageCircle, Send } from "lucide-react";
import { MEDIA, CONTACT } from "../data";

const CONTACT_CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: CONTACT.whatsapp },
  { key: "telegram", label: "Telegram", icon: Send, href: CONTACT.telegram },
  { key: "facebook", label: "Facebook", icon: Facebook, href: CONTACT.facebook },
  { key: "instagram", label: "Instagram", icon: Instagram, href: CONTACT.instagram },
];
import { useLang } from "../LanguageContext";

export const Hero = ({ onCtaClick }) => {
  const { t } = useLang();
  return (
    <section id="top" className="relative min-h-[92vh] flex items-center overflow-hidden grain">
      <div className="absolute inset-0 z-0">
        <video
          className="w-full h-full object-cover ken-burns"
          autoPlay
          muted
          loop
          playsInline
          poster={MEDIA.heroPoster}
        >
          <source src={MEDIA.heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-[#E02020] mb-5">
            {t.hero.overline}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.02] tracking-tighter text-white">
            {t.hero.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-9 flex flex-col gap-5">
            <div className="flex flex-col sm:flex-row sm:items-stretch gap-3">
              <button
                data-testid="hero-cta"
                onClick={onCtaClick}
                className="inline-flex items-center justify-center gap-2 bg-[#E02020] hover:bg-[#B91818] text-white font-medium text-base px-8 py-4 rounded-sm transition-colors sm:flex-1"
              >
                {t.hero.cta}
              </button>
              <a
                href={CONTACT.telegram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-channel-cta"
                className="inline-flex items-center justify-center border border-white/25 hover:bg-white/10 text-white font-medium text-sm px-6 py-4 rounded-sm transition-colors leading-snug text-center sm:flex-1"
              >
                {t.hero.channelCta}
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/70 mb-3">{t.hero.contact}</p>
              <div className="flex flex-wrap gap-2">
                {CONTACT_CHANNELS.map(({ key, label, icon: Icon, href }) => (
                  <a
                    key={key}
                    href={href || undefined}
                    target={href ? "_blank" : undefined}
                    rel={href ? "noopener noreferrer" : undefined}
                    aria-disabled={!href || undefined}
                    tabIndex={href ? undefined : -1}
                    data-testid={`hero-contact-${key}`}
                    onClick={(event) => { if (!href) event.preventDefault(); }}
                    className={`inline-flex items-center gap-2 border border-white/25 text-white font-medium text-sm px-4 py-3 rounded-sm transition-colors ${href ? "hover:bg-white/10" : "opacity-50 cursor-not-allowed"}`}
                  >
                    <Icon size={16} /> {label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70">
            {t.hero.badges.map((b) => <span key={b}>✓ {b}</span>)}
          </div>
        </motion.div>
      </div>

      <a
        href="#oferta"
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-white transition-colors"
        aria-label={t.hero.scroll}
      >
        <ChevronDown size={28} className="animate-bounce" />
      </a>
    </section>
  );
};
